/**
 * variants/solid/DomainIcons.jsx
 * Bespoke SVG glyphs for Homy's 21 core domain modules in "obsidian-solid" variant.
 * Bold geometric solid silhouettes with negative-space cutouts for mobile bottom bars & active tabs.
 */

import * as React from "react";

function createSvgIcon(name, renderPaths) {
  const Component = React.forwardRef(
    ({ size, width, height, className = "", embedded = false, ...props }, ref) => {
      const dimension = size || width || 24;

      if (embedded) {
        return (
          <g
            ref={ref}
            fill="currentColor"
            stroke="none"
            className={className}
            data-icon={name}
            {...props}
          >
            {renderPaths()}
          </g>
        );
      }

      return (
        <svg
          ref={ref}
          viewBox="0 0 24 24"
          width={dimension}
          height={height || dimension}
          fill="currentColor"
          className={className}
          data-icon={name}
          xmlns="http://www.w3.org/2000/svg"
          {...props}
        >
          {renderPaths()}
        </svg>
      );
    }
  );
  Component.displayName = name;
  return Component;
}

// 1. Properties
export const BuildingPropertyIcon = createSvgIcon("BuildingPropertyIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M2.5 3.5A2.5 2.5 0 0 1 5 1h14a2.5 2.5 0 0 1 2.5 2.5V22H15v-5a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v5H2.5V3.5zM7 6h2.5v2.5H7V6zm7.5 0H17v2.5h-2.5V6zM7 11h2.5v2.5H7V11zm7.5 0H17v2.5h-2.5V11z"
  />
));

// 2. Rentals / Leases
export const KeyLeaseIcon = createSvgIcon("KeyLeaseIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M7.5 8.5a5 5 0 1 0 4.9 6H17v3h2v-3h1.5v3.5h2V12.5h-10.1a5 5 0 0 0-4.9-4zm0 3.2a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6z"
  />
));

// 3. Maintenance
export const WrenchServiceIcon = createSvgIcon("WrenchServiceIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M9.3 6.3a1 1 0 0 1 0 1.4l-1.6 1.6a1 1 0 0 1-1.4 0L2.53 5.53a6 6 0 0 0 7.94 7.94l6.91 6.91a2.12 2.12 0 0 0 3-3l-6.91-6.91a6 6 0 0 0-7.94-7.94L9.3 6.3z"
  />
));

// 4. Payments
export const CreditCardPaymentIcon = createSvgIcon("CreditCardPaymentIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M4.5 3.5A2.5 2.5 0 0 0 2 6v12a2.5 2.5 0 0 0 2.5 2.5h15A2.5 2.5 0 0 0 22 18V6a2.5 2.5 0 0 0-2.5-2.5h-15zM2 7.5h20v2H2v-2zm3.5 5h4.2V16H5.5v-3.5zm8 1.5h5v1.8h-5V14z"
  />
));

// 5. Calendar / Visits
export const CalendarClockIcon = createSvgIcon("CalendarClockIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M6.5 1.5v2H4.5A2.5 2.5 0 0 0 2 6v13.5A2.5 2.5 0 0 0 4.5 22h15a2.5 2.5 0 0 0 2.5-2.5V6a2.5 2.5 0 0 0-2.5-2.5h-2v-2h-2v2h-7v-2h-2zM20 8.5H4V6h16v2.5zm-6.5 9a3.8 3.8 0 1 1 7.6 0 3.8 3.8 0 0 1-7.6 0zm4.3-2h-1.5v2.6h2.2v-1.5h-.7v-1.1z"
  />
));

// 6. Tasks
export const CheckSquareTaskIcon = createSvgIcon("CheckSquareTaskIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M5.5 2A3.5 3.5 0 0 0 2 5.5v13A3.5 3.5 0 0 0 5.5 22h13a3.5 3.5 0 0 0 3.5-3.5v-13A3.5 3.5 0 0 0 18.5 2h-13zm5 14l-3.5-3.5 1.41-1.41 2.09 2.08 5.09-5.09 1.41 1.42-6.5 6.5z"
  />
));

// 7. Support
export const LifebuoySupportIcon = createSvgIcon("LifebuoySupportIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M12 1.5a10.5 10.5 0 1 0 0 21 10.5 10.5 0 0 0 0-21zm0 6a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zm-5.8-2.8l2.4 2.4a6.2 6.2 0 0 0-1.3 1.3L4.9 6a8.5 8.5 0 0 1 1.3-1.3zm11.6 0A8.5 8.5 0 0 1 19.1 6l-2.4 2.4a6.2 6.2 0 0 0-1.3-1.3l2.4-2.4zM6.2 19.3l2.4-2.4a6.2 6.2 0 0 0 1.3 1.3L7.5 20.6a8.5 8.5 0 0 1-1.3-1.3zm11.6 0a8.5 8.5 0 0 1-1.3 1.3l-2.4-2.4a6.2 6.2 0 0 0 1.3-1.3l2.4 2.4z"
  />
));

// 8. Applications
export const ClipboardDossierIcon = createSvgIcon("ClipboardDossierIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M8.5 3a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v1H19a2.5 2.5 0 0 1 2.5 2.5v13A2.5 2.5 0 0 1 19 22H5a2.5 2.5 0 0 1-2.5-2.5v-13A2.5 2.5 0 0 1 5 4h3.5V3zm2 0h3v1h-3V3zm-3 7h9v2h-9v-2zm0 4.5h6v2h-6v-2z"
  />
));

// 9. HomyTrust
export const ShieldTrustIcon = createSvgIcon("ShieldTrustIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M12 1.5L3 4.5v7.2c0 6.6 9 10.8 9 10.8s9-4.2 9-10.8V4.5l-9-3zm-1 14l-3.5-3.5 1.41-1.41L11 12.67l5.09-5.08L17.5 9l-6.5 6.5z"
  />
));

// 10. HomySign
export const FileSignatureIcon = createSvgIcon("FileSignatureIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M14.5 1.5H5.5A2.5 2.5 0 0 0 3 4v16a2.5 2.5 0 0 0 2.5 2.5h13A2.5 2.5 0 0 0 21 20V8l-6.5-6.5zm-1 6.5V3.5L19 8h-5.5zm-6 7.5c1.8-1.2 3.5 1.2 4.8 0s2.4-1.2 3.7 0v1.8c-1.8-1.2-3.5 1.2-4.8 0s-2.4-1.2-3.7 0v-1.8z"
  />
));

// 11. Inbox
export const InboxCockpitIcon = createSvgIcon("InboxCockpitIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M4.5 2.5A2.5 2.5 0 0 0 2 5v14a2.5 2.5 0 0 0 2.5 2.5h15A2.5 2.5 0 0 0 22 19V5a2.5 2.5 0 0 0-2.5-2.5h-15zM4 13h3.8l1.7 3h5l1.7-3H20v6H4v-6zm8-3.5a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6z"
  />
));

// 12. Listings
export const LayoutGridListingsIcon = createSvgIcon("LayoutGridListingsIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M3.5 1.5A2 2 0 0 0 1.5 3.5v5.5a2 2 0 0 0 2 2H9a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2H3.5zm11.5 0a2 2 0 0 0-2 2v5.5a2 2 0 0 0 2 2h5.5a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2H15zM3.5 13a2 2 0 0 0-2 2v5.5a2 2 0 0 0 2 2H9a2 2 0 0 0 2-2V15a2 2 0 0 0-2-2H3.5zm11.5 0a2 2 0 0 0-2 2v5.5a2 2 0 0 0 2 2h5.5a2 2 0 0 0 2-2V15a2 2 0 0 0-2-2H15z"
  />
));

// 13. Marketplace
export const StoreMarketplaceIcon = createSvgIcon("StoreMarketplaceIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M3 2.5a1 1 0 0 0-.9 1.45l2 5V20a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.95l2-5A1 1 0 0 0 21 2.5H3zm6.5 17.5v-5h5v5h-5zm7.5-9a2 2 0 1 1-4 0 2 2 0 1 1-4 0 2 2 0 1 1-4 0 2 2 0 1 1-4 0v-.5l1.6-4h14.8l1.6 4V11z"
  />
));

// 14. Memberships
export const CrownTierIcon = createSvgIcon("CrownTierIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M3 17.5L2 6l5.5 4.5L12 2.5l4.5 8L22 6l-1 11.5H3zm-.5 2h19v2a1 1 0 0 1-1 1h-17a1 1 0 0 1-1-1v-2zM12 14a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6z"
  />
));

// 15. Organizations
export const BuildingOrgIcon = createSvgIcon("BuildingOrgIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M2 3a2 2 0 0 1 2-2h6.5a2 2 0 0 1 2 2v3H19a2 2 0 0 1 2 2v13.5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V3zm3.5 3.5h2.5v2H5.5v-2zm0 4.5h2.5v2H5.5v-2zm0 4.5h2.5v2H5.5v-2zm10-4.5H18v2h-2.5v-2zm0 4.5H18v2h-2.5v-2z"
  />
));

// 16. Databases
export const DatabaseStackIcon = createSvgIcon("DatabaseStackIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M12 1.8c-5 0-9 1.4-9 3.2v14c0 1.8 4 3.2 9 3.2s9-1.4 9-3.2V5c0-1.8-4-3.2-9-3.2zm0 2c4.4 0 7 1.05 7 1.2s-2.6 1.2-7 1.2-7-1.05-7-1.2 2.6-1.2 7-1.2zm7 6c-1.3.75-3.9 1.3-7 1.3s-5.7-.55-7-1.3V7.5c1.7.95 4.2 1.55 7 1.55s5.3-.6 7-1.55V9.8zm0 4.6c-1.3.75-3.9 1.3-7 1.3s-5.7-.55-7-1.3V12c1.7.95 4.2 1.55 7 1.55s5.3-.6 7-1.55v2.4z"
  />
));

// 17. Comments
export const MessageThreadIcon = createSvgIcon("MessageThreadIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M12.5 2a9.5 9.5 0 0 0-9.5 9.5c0 1.9.55 3.7 1.5 5.2L3 21.5l4.8-1.5A9.46 9.46 0 0 0 12.5 21a9.5 9.5 0 0 0 0-19zm-4.5 9h9v2H8v-2zm0-3.5h6v2H8v-2z"
  />
));

// 18. Landing Page
export const GlobeWebIcon = createSvgIcon("GlobeWebIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M12 1.5a10.5 10.5 0 1 0 0 21 10.5 10.5 0 0 0 0-21zm0 2c1.4 0 2.7.3 3.8.9-.7 1.6-2 3.4-3.8 5-1.8-1.6-3.1-3.4-3.8-5A8.5 8.5 0 0 1 12 3.5zm-5.8 2.2c.6 1.3 1.7 2.8 3.1 4.1H4.1a8.5 8.5 0 0 1 2.1-4.1zM3.6 12h5.5c-.1.7-.2 1.4-.2 2s.1 1.3.2 2H3.6a8.5 8.5 0 0 1 0-4zm2.6 6.3h3.1c-1.4 1.3-2.5 2.8-3.1 4.1a8.5 8.5 0 0 1-2.1-4.1zM12 20.5c-1.8-1.6-3.1-3.4-3.8-5a15.5 15.5 0 0 1 7.6 0c-.7 1.6-2 3.4-3.8 5zm1.9-6.5c.1-.7.2-1.3.2-2s-.1-1.3-.2-2h5.1a8.5 8.5 0 0 1 0 4h-5.1zm1.2-6.3c1.4-1.3 2.5-2.8 3.1-4.1a8.5 8.5 0 0 1 2.1 4.1h-5.2zm0 8.6h5.2a8.5 8.5 0 0 1-2.1 4.1c-.6-1.3-1.7-2.8-3.1-4.1z"
  />
));

// 19. Settings
export const SlidersSettingsIcon = createSvgIcon("SlidersSettingsIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M2.5 5h3a3 3 0 0 1 6 0h10v2h-10a3 3 0 0 1-6 0h-3V5zm0 6h11a3 3 0 0 1 6 0h2v2h-2a3 3 0 0 1-6 0h-11v-2zm0 6h5a3 3 0 0 1 6 0h8v2h-8a3 3 0 0 1-6 0h-5v-2z"
  />
));

// 20. Core / User
export const UserCockpitIcon = createSvgIcon("UserCockpitIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M12 2a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm-8 19.5a8 8 0 0 1 16 0H4z"
  />
));

// 21. Public
export const CompassPublicIcon = createSvgIcon("CompassPublicIcon", () => (
  <path
    fillRule="evenodd"
    clipRule="evenodd"
    d="M12 1.5a10.5 10.5 0 1 0 0 21 10.5 10.5 0 0 0 0-21zm4 6.5l-2.8 7.5-5.2 1.5 2.8-7.5L16 8z"
  />
));

export const solidDomainGlyphs = {
  properties: BuildingPropertyIcon,
  rentals: KeyLeaseIcon,
  maintenance: WrenchServiceIcon,
  payments: CreditCardPaymentIcon,
  calendar: CalendarClockIcon,
  tasks: CheckSquareTaskIcon,
  support: LifebuoySupportIcon,
  applications: ClipboardDossierIcon,
  trust: ShieldTrustIcon,
  homysign: FileSignatureIcon,
  inbox: InboxCockpitIcon,
  listings: LayoutGridListingsIcon,
  marketplace: StoreMarketplaceIcon,
  memberships: CrownTierIcon,
  organizations: BuildingOrgIcon,
  databases: DatabaseStackIcon,
  comments: MessageThreadIcon,
  landingPage: GlobeWebIcon,
  settings: SlidersSettingsIcon,
  core: UserCockpitIcon,
  public: CompassPublicIcon,
};
